import { Badge, Button } from '@design-system';
import { FundingTable } from '@/components/FundingTable';
import { AddPolicyModal } from '@/components/AddPolicyModal';
import EmptyStateAddButtons from '@/components/EmptyStateAddButtons';
import FundingSourceMetrics from '@/components/FundingSourceMetrics';
import { AnnuityDetailsModal } from '@/components/AnnuityDetailsModal';
import { Citations, citations } from '@/features/client-onboarding/components/citations';
import { AdvisorCustomDisclosures } from '@/features/client-onboarding/components/advisor-custom-disclosures';
import { SelfFundingDetailsModal } from '@/features/self-funding-details-modal';
import { CcrcTypeAManualSheet } from '@/features/ccrc-type-a';
import { AddInsuranceOptionsModal } from '@/components/AddInsuranceOptionsModal';
import { GroupInsurancePlanSelector } from '@/components/GroupInsurancePlanSelector';
import {
  DownloadReportWithPdfOptions,
  OnboardingLayout,
  usePdfReportSections,
  useSlideSetup,
  useFundingSourcesSlide,
  CustomizePlanButtonGroup,
  ExplainSlideButton,
  SelectFundingSourceModal,
} from '@/features/client-onboarding';
import { OnboardingCostFactors } from '@/pages/OnboardingCostFactors';
import { OnboardingFundingCoverage } from '@/pages/OnboardingFundingCoverage';
import { OnboardingCarePhasesSummary } from '@/pages/OnboardingCarePhasesSummary';
import { OnboardingPolicyApplicationDashboard } from '@/pages/OnboardingPolicyApplicationDashboard';

import { GoPlus } from 'react-icons/go';

import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useNavigateToPage } from '@client';

import { useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { EdjPdfEmailSentDialog, usePdfReportDownload } from '@/features/pdf-report';
import { FreemiumPlanningGate, useFreemiumGate } from '@/features/freemium-paywall';
import { navigateToPrintReport } from '@/util/printUtils';

export function OnboardingFundingSources({ clientId }: ClientIdProps) {
  const navigate = useNavigate();
  const { downloadPdfReport, isDownloading, edjEmailSent, dismissEdjEmailSent } =
    usePdfReportDownload();

  const {
    client,
    clientFirstName,
    featureFlags,
    flags,
    showGroupPolicy,
    insuranceState,
    setInsuranceState,
    openSelfFundingModal,
    setOpenSelfFundingModal,
    openAnnuityDetailsModal,
    setOpenAnnuityDetailsModal,
    openSelectFundingSourceModal,
    setOpenSelectFundingSourceModal,
    openAddInsuranceOptionsModal,
    setOpenAddInsuranceOptionsModal,
    openCcrcTypeAManualSheet,
    setOpenCcrcTypeAManualSheet,
    ccrcTypeAEditFundingSourceId,
    selectedSelfFundingSourceId,
    suggestDownloadReportParam,
    handleShowDownloadReport,
    handleOpenShopInsurance,
    openInsuranceDetailsModal,
    closeInsuranceDetailsModal,
    handleClickEditPolicy,
    handleClickEditSelfFunding,
    emptyStateAddOptions,
    fundingSourceAdded,
    showEmptyStateAddFundingSource,
    shouldNavigateToUnderwritingDashboard,
    hasVisitedPersonalizePlan,
  } = useFundingSourcesSlide(clientId);

  const { flags: appConfigFlags } = useFeatureFlags();
  const { isGated: isFreemiumGated, handoffPending: isFreemiumHandoffPending } =
    useFreemiumGate(clientId);
  const navigateToPage = useNavigateToPage();
  const { hydrate } = usePdfReportSections();
  const onboardingPdfReportOptionsEnabled = appConfigFlags.onboardingPdfReportOptions === true;
  useLayoutEffect(() => {
    if (onboardingPdfReportOptionsEnabled) {
      hydrate();
    }
  }, [hydrate, onboardingPdfReportOptionsEnabled]);

  const previousSlidePage = hasVisitedPersonalizePlan
    ? OnboardingCarePhasesSummary
    : OnboardingCostFactors;

  useSlideSetup({
    slidePage: OnboardingFundingSources,
    nextSlidePage:
      shouldNavigateToUnderwritingDashboard && !flags.nycLifeDemo
        ? OnboardingPolicyApplicationDashboard
        : OnboardingFundingCoverage,
    previousSlidePage,
    nextButtonLabel: 'Next',
    disableNextButton: !fundingSourceAdded,
    errorModalMessage: showGroupPolicy
      ? 'Please select a benefit amount to continue'
      : 'Please add a funding source to continue',
  });

  if (isFreemiumGated) {
    return (
      <FreemiumPlanningGate
        clientId={clientId}
        clientFirstName={clientFirstName}
        handoffPending={isFreemiumHandoffPending}
        onBackToPreviousSlide={() => navigateToPage(previousSlidePage, { clientId })}
      />
    );
  }

  return (
    <OnboardingLayout className="max-w-296">
      <div className="flex flex-col justify-center gap-6 py-6">
        <div className="flex w-full flex-wrap items-start justify-between gap-x-6 gap-y-4">
          <h1
            data-testid="funding-sources-header"
            className="text-balance text-2xl font-bold text-gray-950 md:text-4xl"
          >
            {clientFirstName}'s long-term care financing plan
          </h1>
          <div className="ml-auto flex flex-row flex-wrap gap-2 md:shrink-0 md:flex-col md:flex-nowrap md:items-end">
            {featureFlags.flags.ltcFinancialPlanning && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/clients/${clientId}/onboarding/guided-planning`)}
                data-testid="ltc-financial-planning-button"
              >
                Guided Planning
                <Badge>Beta</Badge>
              </Button>
            )}
            {suggestDownloadReportParam && (
              <DownloadReportWithPdfOptions
                compact
                onDownload={
                  flags.reactPdfReport
                    ? downloadPdfReport
                    : () => navigateToPrintReport(client.clientId)
                }
                loading={isDownloading}
              />
            )}
          </div>
        </div>
        <div>
          <div className="flex flex-col">
            <FundingSourceMetrics client={client} />

            <GroupInsurancePlanSelector
              className="mt-6"
              setInsuranceState={setInsuranceState}
            />

            {/* Add funding source options */}
            {showEmptyStateAddFundingSource && (
              <EmptyStateAddButtons
                className="mt-12"
                title="Funding options"
                description={`Quickly add how ${clientFirstName} pays, or plans to pay for long-term care, and we'll calculate the rest.`}
                addOptions={emptyStateAddOptions}
              />
            )}

            {/* combined summary table */}
            {fundingSourceAdded && !showGroupPolicy && (
              <div
                data-testid="funding-table"
                className="mt-6"
              >
                <CustomizePlanButtonGroup
                  client={client}
                  size="default"
                  className="grid grid-cols-2 gap-2 [&_button]:w-full"
                />
                <FundingTable
                  setOpenModal={{
                    ltcPolicy: handleClickEditPolicy,
                    annuity: setOpenAnnuityDetailsModal,
                    selfFunding: handleClickEditSelfFunding,
                  }}
                  handleShowDownloadReport={handleShowDownloadReport}
                  className="mt-4"
                  primaryButton={
                    <Button
                      onClick={() => setOpenSelectFundingSourceModal(true)}
                      data-testid="add-funding-button"
                    >
                      <GoPlus />
                      <span>Add funding</span>
                    </Button>
                  }
                />
              </div>
            )}
          </div>

          <SelectFundingSourceModal
            open={openSelectFundingSourceModal}
            emptyStateAddOptions={emptyStateAddOptions}
            onClose={() => setOpenSelectFundingSourceModal(false)}
          />
          <AddInsuranceOptionsModal
            open={openAddInsuranceOptionsModal}
            onClose={() => setOpenAddInsuranceOptionsModal(false)}
            onAnalyzeExistingPolicy={() => {
              setOpenAddInsuranceOptionsModal(false);
              openInsuranceDetailsModal();
            }}
            onShopNewInsurance={() => {
              setOpenAddInsuranceOptionsModal(false);
              handleOpenShopInsurance();
            }}
            onProcessRateActionLetter={() => {
              setOpenAddInsuranceOptionsModal(false);
              navigate(`/clients/${clientId}/rate-action-letter`);
            }}
            data-testid="shop-insurance-modal-open-button"
          />
          <AddPolicyModal
            open={insuranceState.openInsuranceDetailsModal}
            onClose={closeInsuranceDetailsModal}
            fundingSourceId={insuranceState.selectedPolicyFundingSourceId}
          />
          <AnnuityDetailsModal
            open={openAnnuityDetailsModal}
            setOpen={setOpenAnnuityDetailsModal}
          />
          <SelfFundingDetailsModal
            open={openSelfFundingModal}
            setOpen={setOpenSelfFundingModal}
            fundingSourceId={selectedSelfFundingSourceId}
          />
          <CcrcTypeAManualSheet
            clientId={clientId}
            open={openCcrcTypeAManualSheet}
            onClose={() => setOpenCcrcTypeAManualSheet(false)}
            fundingSourceId={ccrcTypeAEditFundingSourceId}
          />
          <ExplainSlideButton
            videoUrl=""
            articleUrl="https://help.waterlily.com/en/articles/9408213-building-and-modeling-a-funding-plan-for-your-client-s-long-term-care"
            className="hidden md:block"
          />
        </div>
        <div className="mt-auto pt-4">
          <Citations
            className="max-w-296 w-full"
            citations={[citations.OnboardingResultsAreInDisclaimer]}
          />
          <AdvisorCustomDisclosures className="max-w-296 w-full" />
        </div>
      </div>
      <EdjPdfEmailSentDialog
        open={edjEmailSent.isOpen}
        onOpenChange={open => {
          if (!open) dismissEdjEmailSent();
        }}
        advisorEmail={edjEmailSent.advisorEmail}
      />
    </OnboardingLayout>
  );
}

// Re-export types and utilities that moved to the hook but are consumed by other components
export {
  type InsuranceState,
  clientHasPolicyFunding,
  clientHasAnnuityFunding,
  clientHasSelfFunding,
} from '@/features/client-onboarding';
